import { Component, ElementRef, HostListener, Input, ViewChild, ViewEncapsulation } from '@angular/core';

/**
 * The "more" menu at the end of a table row: everything that isn't the row's next step
 * (manage access, view BOM, delete...). One button per row stays visible; the rest live here.
 *
 *   <lb-row-menu [label]="'More for ' + product.productName">
 *     <button type="button" class="lb-menu-item" (click)="openBom(product)"><i class="fa fa-sitemap"></i> Build of materials</button>
 *     <button type="button" class="lb-menu-item lb-menu-item--danger" (click)="remove(product)"><i class="fa fa-trash"></i> Delete</button>
 *   </lb-row-menu>
 *
 * The menu is positioned against the viewport (fixed), so a scrolling table can't clip it.
 * Closes on a choice, Escape, or a click anywhere else; arrow keys move between items.
 */
@Component({
  selector: 'lb-row-menu',
  encapsulation: ViewEncapsulation.None,
  template: `
    <button #trigger type="button" class="btn btn-sm btn-outline-secondary lb-row-menu-trigger"
            [attr.aria-label]="label" [attr.aria-expanded]="open" aria-haspopup="menu" (click)="toggle()">
      <i class="fa fa-ellipsis-h" aria-hidden="true"></i>
    </button>
    <div #menu class="lb-row-menu" role="menu" [attr.aria-label]="label" *ngIf="open"
         [style.top.px]="top" [style.left.px]="left" (click)="onMenuClick($event)" (keydown)="onKey($event)">
      <ng-content></ng-content>
    </div>
  `,
  styles: [`
    lb-row-menu { display: inline-block; }
    .lb-row-menu {
      position: fixed;
      z-index: 1060;
      min-width: 12rem;
      padding: var(--lb-space-1) 0;
      background: var(--lb-surface);
      border: 1px solid var(--lb-line);
      border-radius: var(--lb-radius-sm);
      box-shadow: var(--lb-shadow-float);
    }
    .lb-menu-item {
      display: flex;
      align-items: center;
      gap: var(--lb-space-2);
      width: 100%;
      padding: 0.5rem var(--lb-space-3);
      border: 0;
      background: transparent;
      color: var(--lb-text);
      text-align: left;
      white-space: nowrap;
      min-height: 2.5rem;
      text-decoration: none;
    }
    .lb-menu-item:hover,
    .lb-menu-item:focus {
      background: var(--lb-surface-2);
      color: var(--lb-text);
      outline: none;
    }
    .lb-menu-item .fa { width: 1rem; color: var(--lb-text-muted); }
    .lb-menu-item--danger,
    .lb-menu-item--danger .fa { color: var(--lb-bad); }
    .lb-menu-item:disabled { color: var(--lb-text-faint); }
    .lb-menu-divider { height: 1px; margin: var(--lb-space-1) 0; background: var(--lb-line); }
  `]
})
export class LbRowMenuComponent {
  /** Accessible name of the trigger and menu, e.g. "More for Harbour Cafe" */
  @Input() label = 'More actions';

  @ViewChild('trigger') trigger!: ElementRef<HTMLButtonElement>;
  @ViewChild('menu') menu?: ElementRef<HTMLElement>;

  open = false;
  top = 0;
  left = 0;

  constructor(private host: ElementRef<HTMLElement>) { }

  toggle(): void {
    if (this.open) {
      this.close(false);
      return;
    }
    const r = this.trigger.nativeElement.getBoundingClientRect();
    this.left = Math.max(8, Math.min(r.right - 208, window.innerWidth - 216));
    this.top = r.bottom + 4;
    this.open = true;
    setTimeout(() => {
      const menu = this.menu?.nativeElement;
      if (!menu) return;
      // No room below: open upwards
      const height = menu.offsetHeight;
      if (r.bottom + 4 + height > window.innerHeight - 8) this.top = Math.max(8, r.top - height - 4);
      const items = this.items();
      items.forEach(i => i.setAttribute('role', 'menuitem'));
      if (items.length) items[0].focus();
    });
  }

  close(returnFocus = true): void {
    this.open = false;
    if (returnFocus) this.trigger?.nativeElement.focus();
  }

  onMenuClick(event: Event): void {
    const item = (event.target as HTMLElement).closest('.lb-menu-item');
    if (item && !(item as HTMLButtonElement).disabled) this.close(false);
  }

  onKey(event: KeyboardEvent): void {
    const items = this.items();
    const at = items.indexOf(document.activeElement as HTMLElement);
    if (event.key === 'ArrowDown') { items[(at + 1) % items.length]?.focus(); event.preventDefault(); }
    if (event.key === 'ArrowUp') { items[(at - 1 + items.length) % items.length]?.focus(); event.preventDefault(); }
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.open) this.close();
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    if (this.open && !this.host.nativeElement.contains(event.target as Node)) this.close(false);
  }

  /** Scrolling or resizing would leave a fixed menu behind its row */
  @HostListener('window:resize')
  @HostListener('window:scroll')
  onViewportChange(): void {
    if (this.open) this.close(false);
  }

  private items(): HTMLElement[] {
    return this.menu ? Array.from(this.menu.nativeElement.querySelectorAll<HTMLElement>('.lb-menu-item:not(:disabled)')) : [];
  }
}
